import React, { useState, useEffect } from "react";
import CustomModel from "../CustomModel";
import { styles } from "../../styles/style";
import Status from "../../utils/kycUtils";
import Lightbox from "react-awesome-lightbox";
import "react-awesome-lightbox/build/style.css";
import { FiDownload } from "react-icons/fi";
import * as XLSX from "xlsx";
import { useChangeKycMutation } from "../../redux/features/user/userApi";

const PersonalInfo = ({
  data,
  handleApprove,
  handleReject,
  isProcessing,
  reason,
  setReason,
  rejectDialogOpen,
  setRejectDialogOpen,
  handleRejectConfirm,
  handleImageViewer,
  updatedStatus,
  setUpdatedStatus,
  canWrite,
  id // add id prop
}) => {
  const [currentPage, setCurrentPage] = useState("");
  const [localStatuses, setLocalStatuses] = useState({
    proof: data?.kyc?.proofstatus,
    pan: data?.kyc?.panstatus,
    selfie: data?.kyc?.selfiestatus
  });

  const [localVerifiedBy, setLocalVerifiedBy] = useState({
    proof: data?.kyc?.proofVerifiedBy,
    pan: data?.kyc?.panVerifiedBy,
    selfie: data?.kyc?.selfieVerifiedBy
  });

  // Check if current user is admin
  const isAdmin = () => {
    const role = localStorage.getItem("role");
    return role === "admin";
  };

  // Check if all proofs are verified
  const allVerified = localStatuses.proof === 1 && localStatuses.pan === 1 && localStatuses.selfie === 1;

  // Update local statuses and verified by when data changes
  useEffect(() => {
    if (data?.kyc) {
      setLocalStatuses({
        proof: data.kyc.proofstatus,
        pan: data.kyc.panstatus,
        selfie: data.kyc.selfiestatus
      });
      setLocalVerifiedBy({
        proof: data.kyc.proofVerifiedBy,
        pan: data.kyc.panVerifiedBy,
        selfie: data.kyc.selfieVerifiedBy
      });
    }
  }, [data]);

  // Handle immediate status updates
  useEffect(() => {
    if (updatedStatus) {
      setLocalStatuses(prev => ({
        ...prev,
        [updatedStatus.page]: updatedStatus.status
      }));
      // Update verified by when status changes to approved
      if (updatedStatus.status === 1) {
        setLocalVerifiedBy(prev => ({
          ...prev,
          [updatedStatus.page]: localStorage.getItem('username') || 'Admin'
        }));
      } else if (updatedStatus.status === 0) {
        setLocalVerifiedBy(prev => ({
          ...prev,
          [updatedStatus.page]: null
        }));
      }
      setUpdatedStatus(null);
    }
  }, [updatedStatus, setUpdatedStatus]);

  // Add null check and default values for data destructuring
  const {
    unique_id = '',
    kait_id = '',
    username = '',
    firstname = '',
    lastname = '',
    email = '',
    dob = '',
    age = '',
    gender = '',
    phone = '',
    pincode = '',
    country = '',
    state = '',
    city = '',
    address = '',
  } = data || {};

  const {
    back = '',
    front = '',
    pan = '',
    selfie = '',
    proofname = '',
    proofnumber = '',
    pannumber = '',
    proofVerifiedBy = '',
    Date = '',
    selfieVerifiedBy = '',
    date = ''
  } = data?.kyc || {};

  const [openProof, setOpenProof] = useState(false);
  const [openPan, setOpenPan] = useState(false);
  const [openSelfie, setOpenSelfie] = useState(false);
  const [openBack, setOpenBack] = useState(false);
  const [openFront, setOpenFront] = useState(false);

  const fields = [
    { field: "UniqueId", text: unique_id },
    { field: "KaitId", text: kait_id },
    { field: "Username", text: username },
    { field: "FirstName", text: firstname },
    { field: "LastName", text: lastname },
    { field: "Email", text: email },
    { field: "Date of Birth", text: dob },
    { field: "Age", text: age },
    { field: "Gender", text: gender },
    { field: "Phone Number", text: phone },
    { field: "Address", text: address },
    { field: "City", text: city },
    { field: "State", text: state },
    { field: "Country", text: country },
    { field: "Pincode", text: pincode },
  ];

  // Export handler for personal and KYC info
  const handleExport = () => {
    const personalInfo = fields.map(f => ({ Field: f.field, Value: f.text }));
    const kycInfo = [
      { Field: "Proof Name", Value: proofname },
      { Field: "Proof Number", Value: proofnumber },
      { Field: "Pan Number", Value: pannumber },
      { Field: "Proof Status", Value: localStatuses.proof === 1 ? "Verified" : localStatuses.proof === 0 ? "Rejected" : "Pending" },
      { Field: "Proof VerifiedBy", Value: localVerifiedBy.proof || "Not Provided" },
      { Field: "Proof Reject Reason", Value: data?.kyc?.proofRejectReason || "N/A" },
      { Field: "Pan Status", Value: localStatuses.pan === 1 ? "Verified" : localStatuses.pan === 0 ? "Rejected" : "Pending" },
      { Field: "Pan VerifiedBy", Value: localVerifiedBy.pan || "Not Provided" },
      { Field: "Pan Reject Reason", Value: data?.kyc?.panRejectReason || "N/A" },
      { Field: "Selfie Status", Value: localStatuses.selfie === 1 ? "Verified" : localStatuses.selfie === 0 ? "Rejected" : "Pending" },
      { Field: "Selfie VerifiedBy", Value: localVerifiedBy.selfie || "Not Provided" },
      { Field: "Selfie Reject Reason", Value: data?.kyc?.selfieRejectReason || "N/A" },
    ];
    // Combine both personal and KYC info into a single array
    const combinedInfo = [
      { Field: "--- Personal Information ---", Value: "" },
      ...personalInfo,
      { Field: "--- KYC Document Information ---", Value: "" },
      ...kycInfo
    ];
    const ws = XLSX.utils.json_to_sheet(combinedInfo, { header: ["Field", "Value"] });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Personal & KYC Info");
    XLSX.writeFile(wb, `Personal_KYC_Info_${username || "user"}.xlsx`);
  };

  const renderImage = (image, setOpen, isOpen) => {
    if (!image) {
      return (
        <p className="text-red-500 border-2 font-medium w-fit p-1 border-red-500">
          Not Provided
        </p>
      );
    }
    // Download handler for image
    const handleDownloadImage = () => {
      const link = document.createElement('a');
      link.href = image;
      link.download = 'kyc_document.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
    return (
      <React.Fragment>
        <img
          src={image}
          className="w-[70px] h-[70px] rounded-full object-contain cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => setOpen(true)}
          alt="Document"
        />
        {isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="relative max-w-4xl w-full mx-4">
              <button
                onClick={() => setOpen(false)}
                className="absolute -top-10 right-0 text-white hover:text-gray-300 focus:outline-none"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <img
                src={image}
                className="w-full h-auto max-h-[80vh] object-contain"
                alt="Document"
              />
              <button
                onClick={handleDownloadImage}
                className="mt-4 bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 flex items-center gap-2 shadow-lg"
                style={{ position: 'absolute', bottom: 16, right: 16 }}
              >
                <FiDownload className="w-5 h-5" /> Download
              </button>
            </div>
          </div>
        )}
      </React.Fragment>
    );
  };

  const handleLocalApprove = async (page) => {
    try {
      // Update local state immediately
      setLocalStatuses(prev => ({
        ...prev,
        [page]: 1 // Set to verified status
      }));

      // Update verified by immediately with current user
      const currentUser = localStorage.getItem('username') || 'Admin';
      setLocalVerifiedBy(prev => ({
        ...prev,
        [page]: currentUser
      }));

      // Call the API
      const result = await handleApprove(page);

      // If API call fails, revert the local state
      if (!result?.status) {
        setLocalStatuses(prev => ({
          ...prev,
          [page]: 2 // Set back to pending status
        }));
        setLocalVerifiedBy(prev => ({
          ...prev,
          [page]: data?.kyc?.[`${page}VerifiedBy`]
        }));
      }
    } catch (error) {
      // Revert local state if API call fails
      setLocalStatuses(prev => ({
        ...prev,
        [page]: 2 // Set back to pending status
      }));
      setLocalVerifiedBy(prev => ({
        ...prev,
        [page]: data?.kyc?.[`${page}VerifiedBy`]
      }));
    }
  };

  const handleLocalReject = (page) => {
    setCurrentPage(page);
    // Clear any existing verified by when rejecting
    setLocalVerifiedBy(prev => ({
      ...prev,
      [page]: null
    }));
    handleReject(page);
  };
  const [changeKyc, { isLoading: isChangeKycLoading }] = useChangeKycMutation();
  const handleLocaledit = async (page) => {
    try {
      const response = await changeKyc({ id, page, body: {} }).unwrap();
      console.log('Edit KYC response:', response);
      window.location.reload()

      // Optionally, handle UI updates or show a toast here
    } catch (error) {
      console.error('Edit KYC error:', error);
      // Optionally, show an error toast here
    }
  };

  // Update effect to handle rejections
  useEffect(() => {
    if (rejectDialogOpen === false && currentPage && reason) {
      // Update local status when rejection is successful
      setLocalStatuses(prev => ({
        ...prev,
        [currentPage]: 0 // Set to rejected status
      }));
      // Clear verified by on rejection
      setLocalVerifiedBy(prev => ({
        ...prev,
        [currentPage]: null
      }));
      setCurrentPage(""); // Reset current page
    }
  }, [rejectDialogOpen, currentPage, reason]);

  const renderStatus = (statusCode, page) => {
    const currentStatus = localStatuses[page];

    if (currentStatus === 0) {
      return (
        <div className="flex flex-col gap-1">
          <p className="text-red-500 bg-red-50 border font-medium w-fit p-1 border-red-500">
            Rejected
          </p>
          {reason && page === currentPage && (
            <p className="text-sm text-gray-600">Reason: {reason}</p>
          )}
        </div>
      );
    } else if (currentStatus === 1) {
      return (
        <div className="flex gap-x-2 align-items-center">
          <p className="text-green-500 bg-green-50 rounded-md border font-medium w-fit p-1 border-green-500" >
            Verified
          </p >
          {canWrite && isAdmin() && (
            <button
              type="button"
              className={`${styles.button} !bg-blue-500 !w-fit disabled:opacity-50`}
              onClick={() => handleLocaledit(page)}
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Edit"}
            </button>
          )}
        </div >
      );
    } else if (currentStatus === 2) {
      return (
        <div className="flex gap-4">
          {canWrite && (
            <button
              type="button"
              className={`${styles.button} !bg-green-500 !w-fit disabled:opacity-50`}
              onClick={() => handleLocalApprove(page)}
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Approve"}
            </button>
          )}
          {canWrite && (
            <button
              type="button"
              className={`${styles.button} !bg-red-500 !w-fit disabled:opacity-50`}
              onClick={() => handleLocalReject(page)}
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Reject"}
            </button>
          )}
        </div>
      );
    } else if (currentStatus === 3) {
      return (
        <p className="text-red-500 border-2 font-medium w-fit p-1 border-red-500">
          Not Provided
        </p>
      );
    }
    return null;
  };

  const renderActionButtons = (page, status) => {
    if (!canWrite) return null;

    return (
      <div className="flex gap-2">
        <button
          className={`px-4 py-2 rounded ${status === 1
            ? "bg-green-500 text-white cursor-not-allowed opacity-50"
            : "bg-green-500 text-white hover:bg-green-600"
            }`}
          onClick={() => handleLocalApprove(page)}
          disabled={status === 1 || isProcessing}
        >
          {isProcessing ? "Processing..." : "Approve"}
        </button>
        <button
          className={`px-4 py-2 rounded ${status === 0
            ? "bg-red-500 text-white cursor-not-allowed opacity-50"
            : "bg-red-500 text-white hover:bg-red-600"
            }`}
          onClick={() => handleLocalReject(page)}
          disabled={status === 0 || isProcessing}
        >
          {isProcessing ? "Processing..." : "Reject"}
        </button>
      </div>
    );
  };

  return (
    <div className="bg-slate-100 flex flex-col md:flex-row">
      <div className="md:m-2 w-full md:w-[50%]">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-lg py-2 text-blue-900">Personal Information</h2>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded shadow text-sm"
          >
            <FiDownload className="w-5 h-5" />
            {/* Export */}
          </button>
        </div>
        <div className="w-full bg-white rounded-md border">
          {fields.map((item, index) => (
            <div key={index} className="flex h-10 p-2 items-center border-b-[1px] select-text">
              <p className="w-[50%] font-medium">{item.field}</p>
              {item.text ? (
                <p className="select-all">{item.text}</p>
              ) : (
                <p className="text-red-500">Not Provided</p>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="md:m-2 md:ml-0 w-full md:w-[50%]">
        <div className={`w-full bg-white border rounded-md transition-all duration-300 ${allVerified ? 'border-2 border-green-500 shadow-md' : 'border-gray-200'}`}>
          <div className={`rounded-t-md h-10 text-black font-medium p-2 select-text flex items-center justify-between ${allVerified ? 'bg-green-50' : 'bg-gray-200'}`}>
            <span>Document Information</span>
            {allVerified && (
              <span className="text-green-700 font-semibold bg-green-100 px-3 py-1 rounded-full">
                COMPLETELY VERIFIED
              </span>
            )}
          </div>

          <div className="flex h-10 p-2 items-center border-b-[1px] select-text">
            <p className="w-[50%] font-medium">Proof Name</p>
            {proofname ? (
              <p className="select-all">{proofname}</p>
            ) : (
              <p className="text-red-500 border bg-red-50 rounded-sm font-medium w-fit p-1 border-red-500">
                Not Provided
              </p>
            )}
          </div>

          <div className="flex h-10 p-2 items-center border-b-[1px] select-text">
            <p className="w-[50%] font-medium">Proof Number</p>
            <p className="select-all">{proofnumber || "Not Provided"}</p>
          </div>

          <div className="flex h-fit p-2 items-center border-b-[1px]">
            <p className="w-[50%] font-medium">Front</p>
            {renderImage(front, setOpenFront, openFront)}
          </div>

          <div className="flex h-fit p-2 items-center border-b-[1px]">
            <p className="w-[50%] font-medium">Back</p>
            {renderImage(back, setOpenBack, openBack)}
          </div>

          <div className="flex h-fit p-2 items-center border-b-[1px]">
            <p className="w-[50%] font-medium">Proof Status</p>
            <div className="flex flex-col">
              {renderStatus(localStatuses.proof, "proof")}
              {/* Show rejection reason if rejected */}
              {localStatuses.proof === 0 && (
                <p className="text-xs text-red-600 mt-1">
                  Reason: {data?.kyc?.proofinfo || "N/A"}
                </p>
              )}
            </div>
          </div>

          <div className="flex h-fit p-2 items-center border-b-[1px] select-text">
            <p className="w-[50%] font-medium">Proof VerifiedBy</p>
            {localVerifiedBy.proof ? (
              <p className="font-semibold select-all">{localVerifiedBy.proof}</p>
            ) : (
              <p className="text-red-500 border-2 font-medium w-fit p-1 border-red-500">
                Not Provided
              </p>
            )}
          </div>


          <div className="flex h-fit p-2 items-center border-b-[1px]">
            <p className="w-[50%] font-medium">Pan</p>
            {renderImage(pan, setOpenPan, openPan)}
          </div>
          <div className="flex h-10 p-2 items-center border-b-[1px] select-text">
            <p className="w-[50%] font-medium">Pan Number</p>
            <p className="select-all">{pannumber || "Not Provided"}</p>
          </div>

          <div className="flex h-fit p-2 items-center border-b-[1px]">
            <p className="w-[50%] font-medium">Pan Status</p>
            <div className="flex flex-col">
              {renderStatus(localStatuses.pan, "pan")}
              {/* Show rejection reason if rejected */}
              {localStatuses.pan === 0 && (
                <p className="text-xs text-red-600 mt-1">
                  Reason: {data?.kyc?.paninfo || "N/A"}
                </p>
              )}
            </div>
          </div>

          <div className="flex h-fit p-2 items-center border-b-[1px] select-text">
            <p className="w-[50%] font-medium">Pan VerifiedBy</p>
            {localVerifiedBy.pan ? (
              <p className="font-semibold select-all">{localVerifiedBy.pan}</p>
            ) : (
              <p className="text-red-500 border-2 font-medium w-fit p-1 border-red-500">
                Not Provided
              </p>
            )}
          </div>

          <div className="flex h-fit p-2 items-center border-b-[1px]">
            <p className="w-[50%] font-medium">Selfie</p>
            {renderImage(selfie, setOpenSelfie, openSelfie)}
          </div>

          <div className="flex h-fit p-2 items-center border-b-[1px]">
            <p className="w-[50%] font-medium">Selfie Status</p>
            <div className="flex flex-col">
              {renderStatus(localStatuses.selfie, "selfie")}
              {/* Show rejection reason if rejected */}
              {localStatuses.selfie === 0 && (
                <p className="text-xs text-red-600 mt-1">
                  Reason: {data?.kyc?.selfieinfo || "N/A"}
                </p>
              )}
            </div>
          </div>

          <div className="flex h-fit p-2 items-center border-b-[1px] select-text">
            <p className="w-[50%] font-medium">Selfie VerifiedBy</p>
            {localVerifiedBy.selfie ? (
              <p className="font-semibold select-all">{localVerifiedBy.selfie}</p>
            ) : (
              <p className="text-red-500 border-2 font-medium w-fit p-1 border-red-500">
                Not Provided
              </p>
            )}
          </div>

          <div className="flex h-fit p-2 items-center justify-center border-b-[1px] select-text">
            <p className="w-[50%] font-medium">Kyc Submitted @ {data?.kyc?.Date || 'Not submitted'}</p>
          </div>
          {/* Verification status bar at the bottom */}
          {allVerified && (
            <div className="bg-green-50 text-green-800 p-2 text-center font-medium rounded-b-md">
              All documents have been successfully verified
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonalInfo;